import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants/config';

interface AuthenticatedImageProps {
  itemId: number;
  isLabelImage?: boolean;
  alt: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  className?: string;
}

const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
  itemId,
  isLabelImage = false,
  alt,
  style,
  onClick,
  className
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    // Use Intersection Observer for lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !imageUrl && !hasError) {
            loadImage();
            observer.disconnect();
          }
        });
      },
      { rootMargin: '50px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
      observerRef.current = observer;
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [itemId, isLabelImage]);

  const loadImage = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const endpoint = isLabelImage
        ? `${API_BASE_URL}/inventory-items/${itemId}/label-image`
        : `${API_BASE_URL}/inventory-items/${itemId}/image`;

      const response = await axios.get(endpoint, {
        responseType: 'blob',
        headers: { Authorization: token }
      });

      const url = URL.createObjectURL(response.data);
      setImageUrl(url);
      setHasError(false);
    } catch (error) {
      console.error('Error loading image:', error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (hasError) {
    return (
      <div ref={imgRef} style={style} className={className}>
        <span style={{ color: '#999', fontSize: '0.75rem' }}>No image</span>
      </div>
    );
  }

  if (isLoading || !imageUrl) {
    return (
      <div
        ref={imgRef}
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5'
        }}
        className={className}
      >
        <span style={{ color: '#999', fontSize: '0.6rem' }}>...</span>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      style={style}
      onClick={onClick}
      className={className}
      loading="lazy"
    />
  );
};

export default AuthenticatedImage;

